import { useState } from "react";
import { UserPlus, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { FieldText, FieldGrade, FieldSection, FieldAcademicYear, FieldEnrollmentStatus } from "../components/StudentFormFields";
import { createNewStudent } from "../lib/api";
import { CURRENT_ACADEMIC_YEAR } from "../lib/constants";

const initial = () => ({
  name: "",
  national_id: "",
  phone: "",
  grade: "",
  section: "",
  academic_year: CURRENT_ACADEMIC_YEAR,
  registration_date: new Date().toISOString().slice(0, 10),
  enrollment_status: "مستجد",
});

export default function NewStudentForm() {
  const [form, setForm] = useState(initial());
  const [loading, setLoading] = useState(false);

  const setField = (k) => (v) => setForm(prev => ({ ...prev, [k]: v, ...(k === "grade" ? { section: "" } : {}) }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.guardian_phone || !form.grade || !form.section || !form.academic_year || !form.registration_date) {
      toast.error("الرجاء تعبئة جميع الحقول المطلوبة (بما فيها رقم ولي الأمر)");
      return;
    }
    if (form.national_id && !/^[0-9]{8,15}$/.test(form.national_id)) {
      toast.error("رقم الهوية يجب أن يكون أرقاماً (8-15 رقم) — أو اتركه فارغاً");
      return;
    }
    if (!/^[0-9+]{7,15}$/.test(form.phone)) {
      toast.error("رقم الجوال غير صالح");
      return;
    }
    if (!/^[0-9+]{7,15}$/.test(form.guardian_phone)) {
      toast.error("رقم جوال ولي الأمر غير صالح");
      return;
    }
    setLoading(true);
    try {
      await createNewStudent(form);
      toast.success("تم تسجيل الطالب المستجد بنجاح");
      setForm(initial());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "حدث خطأ أثناء الحفظ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader title="تسجيل طالب مستجد" subtitle="إدخال بيانات طالب جديد في النظام" icon={UserPlus} />
      <Card className="p-4 sm:p-6 lg:p-8 border-slate-200 shadow-sm">
        <form onSubmit={onSubmit} className="space-y-4 sm:space-y-5" data-testid="new-student-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            <FieldText id="name" label="اسم الطالب الرباعي" value={form.name} onChange={setField("name")} placeholder="أدخل الاسم كاملاً" required testid="input-name" />
            <FieldText id="national_id" label="رقم الهوية (اختياري)" value={form.national_id} onChange={setField("national_id")} placeholder="مثال: 1012345678" testid="input-national-id" />
            <FieldText id="phone" label="رقم جوال الطالب" value={form.phone} onChange={setField("phone")} placeholder="05xxxxxxxx" required testid="input-phone" />
            <FieldText id="guardian_name" label="اسم ولي الأمر (اختياري)" value={form.guardian_name} onChange={setField("guardian_name")} placeholder="اسم الأب أو الأم" testid="input-guardian-name" />
            <FieldText id="guardian_phone" label="رقم جوال ولي الأمر (واتساب)" value={form.guardian_phone} onChange={setField("guardian_phone")} placeholder="05xxxxxxxx" required testid="input-guardian-phone" />
            <FieldGrade value={form.grade} onChange={setField("grade")} />
            <FieldSection grade={form.grade} value={form.section} onChange={setField("section")} />
            <FieldAcademicYear value={form.academic_year} onChange={setField("academic_year")} />
            <FieldText id="registration_date" label="تاريخ التسجيل" type="date" value={form.registration_date} onChange={setField("registration_date")} required testid="input-reg-date" />
            <FieldEnrollmentStatus value={form.enrollment_status} onChange={setField("enrollment_status")} />
          </div>
          <div className="gold-divider opacity-60 my-4" />
          <div className="flex flex-wrap gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => setForm(initial())} className="h-11 gap-2" data-testid="btn-reset">
              <RotateCcw size={16} /> مسح الحقول
            </Button>
            <Button type="submit" disabled={loading} className="h-11 gap-2 bg-teal-700 hover:bg-teal-800 text-white btn-elevate" data-testid="btn-submit-new">
              <Save size={16} /> {loading ? "جاري الحفظ..." : "حفظ بيانات الطالب"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
